import { WaypointActionType } from '@caresend/types';
import { arrayToObj, capitalize, initWaypointAction } from '@caresend/utils';
import update from 'immutability-helper';
import cloneDeep from 'lodash.clonedeep';
import { defineComponent, computed, ref, watchEffect, watch } from 'vue';
import __vue_component__ from '../../../../../inputs/ListAddRemoveItem.vue.js';
import __vue_component__$1 from './ManualWaypointAction.vue.js';
import { usePortalStore } from '../../../store/portal/index.js';
import { getStore } from '../../../../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'ManualWaypointActions',
    components: {
        ListAddRemoveItem: __vue_component__,
        ManualWaypointAction: __vue_component__$1,
    },
    model: {
        prop: 'inputWaypointActions',
        event: 'update',
    },
    props: {
        inputWaypointActions: {
            type: Array,
            default: () => [],
        },
        isWaypointInItineraryDraft: Boolean,
        waypointID: {
            type: String,
            required: true,
        },
    },
    setup(props, { emit }) {
        var _a, _b;
        const store = getStore();
        const portalStore = usePortalStore();
        const waypoint = computed(() => portalStore.waypoints.get(props.waypointID));
        const placeGroup = computed(() => {
            var _a, _b, _c, _d;
            if (!((_a = waypoint.value) === null || _a === void 0 ? void 0 : _a.placeID))
                return;
            const placeGroupID = (_c = (_b = store.state.places.places) === null || _b === void 0 ? void 0 : _b[waypoint.value.placeID]) === null || _c === void 0 ? void 0 : _c.placeGroupID;
            if (!placeGroupID)
                return;
            return (_d = store.state.places.placeGroups) === null || _d === void 0 ? void 0 : _d[placeGroupID];
        });
        const defaultSupportedTypes = Object.values(WaypointActionType)
            .filter((wpaType) => wpaType !== WaypointActionType.PATIENT_ACTION);
        const supportedTypes = ref((_b = (_a = placeGroup.value) === null || _a === void 0 ? void 0 : _a.supportedActionTypes) !== null && _b !== void 0 ? _b : defaultSupportedTypes);
        const waypointActions = ref(cloneDeep(props.inputWaypointActions));
        /** This is used to determine the validity of added procedures within the logical context of each waypoint action */
        const localContext = ref(arrayToObj(waypointActions.value, 'id'));
        /**
         * When deleting a waypoint action, the id of the object should be retained in the local context.
         * Without this, the validity clause for procedure actions falls back to the state of the itinerary which
         * at this point is outdated.
         */
        watchEffect(() => {
            localContext.value = {
                ...localContext.value,
                ...arrayToObj(waypointActions.value, 'id'),
            };
        });
        const addWaypointAction = () => {
            const initWpaType = supportedTypes.value[0];
            if (!initWpaType)
                return;
            const waypointAction = initWaypointAction(initWpaType, props.waypointID);
            const itineraryID = portalStore.itinerary.value.id;
            waypointAction.itineraryID = itineraryID;
            waypointActions.value.push(waypointAction);
            emit('update', waypointActions.value);
        };
        const deleteWaypointAction = (index) => {
            const waypointAction = waypointActions.value.splice(index, 1)[0];
            if (!(waypointAction === null || waypointAction === void 0 ? void 0 : waypointAction.id))
                return;
            localContext.value[waypointAction.id] = null;
            emit('delete', waypointAction.id);
        };
        const commitShipments = ref({});
        watch(waypointActions, () => {
            if (!waypointActions.value.length)
                addWaypointAction();
            emit('update', {
                wpas: waypointActions.value,
                commitShipments: commitShipments.value,
            });
        }, { deep: true, immediate: true });
        const updateWpa = ({ wpa, ...input }, index) => {
            var _a;
            const newWpas = update(waypointActions.value, {
                [index]: {
                    $set: wpa,
                },
            });
            waypointActions.value = newWpas;
            commitShipments.value[wpa.id] = (_a = input.commitShipments) !== null && _a !== void 0 ? _a : false;
        };
        return {
            addWaypointAction,
            capitalize,
            deleteWaypointAction,
            localContext,
            supportedTypes,
            updateWpa,
            waypointActions,
        };
    },
});

export { script as default };
